import React, { useState, useEffect } from "react";
import {
  Title,
  Table,
  Button,
  Badge,
  Group,
  Modal,
  Select,
  NumberInput,
  ActionIcon,
  Loader,
  Center,
  Text,
  Box,
  Tooltip,
  ScrollArea,
  ThemeIcon,
  Alert,
  Paper // Tambahkan Paper
} from "@mantine/core";
import { DateInput } from '@mantine/dates';
import { useDisclosure } from "@mantine/hooks";
import { 
  IconPlus, 
  IconTruckDelivery, 
  IconTrash, 
  IconInfoCircle,
  IconBuilding, 
  IconChefHat,
  IconRefresh // Tambahkan Icon Refresh
} from "@tabler/icons-react";

import api from "../../services/api";

// --- Helper Warna Status ---
const statusColors = {
  diterima: 'green',
  dikirim: 'blue',
  disiapkan: 'yellow',
  dibatalkan: 'red'
};

export default function DistribusiPenyediaPage() {
  // --- State Data ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // --- State Dropdown ---
  const [sekolahOptions, setSekolahOptions] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);

  // --- State Modal ---
  const [opened, { open, close }] = useDisclosure(false);
  const [submitting, setSubmitting] = useState(false);
  
  // --- State Form ---
  const [form, setForm] = useState({
    sekolah_id: null,
    menu_id: null,
    tanggal_distribusi: new Date(),
    jumlah: 100
  });

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/penyedia/distribusi");
      setData(res.data.data || []);
      
      // Ambil Options untuk Dropdown
      const resOpt = await api.get("/penyedia/distribusi/options");
      setSekolahOptions(resOpt.data.sekolahs.map(s => ({ 
        value: String(s.id), label: s.nama_sekolah 
      })));
      setMenuOptions(resOpt.data.menus.map(m => ({ 
        value: String(m.id), label: m.nama_menu 
      })));

    } catch (error) {
      console.error("Gagal load data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 2. HANDLERS ---

  const handleSubmit = async () => {
    if (!form.sekolah_id || !form.menu_id || !form.tanggal_distribusi) {
      alert("Mohon lengkapi semua data.");
      return;
    }
    setSubmitting(true);
    try {
      const dateString = form.tanggal_distribusi.toLocaleDateString('en-CA');
      const payload = {
        sekolah_id: form.sekolah_id,
        menu_id: form.menu_id,
        tanggal_distribusi: dateString,
        jumlah: form.jumlah
      };
      await api.post("/penyedia/distribusi", payload);
      
      close();
      fetchData();
      setForm({ ...form, jumlah: 100, sekolah_id: null, menu_id: null });
      
    } catch (error) {
      console.error(error);
      alert("Gagal menyimpan data.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKirim = async (id) => {
    if (!window.confirm("Kirim barang sekarang? Status akan berubah menjadi 'Dikirim'.")) return;
    try {
      await api.put(`/penyedia/distribusi/${id}`, { status: 'dikirim' });
      fetchData();
    } catch (error) {
      alert("Gagal update status.");
    }
  };

  const handleHapus = async (id) => {
    if (!window.confirm("Yakin hapus jadwal ini?")) return;
    try {
      await api.delete(`/penyedia/distribusi/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || "Gagal menghapus.");
    }
  };

  // --- 3. RENDER UI ---

  if (loading) return <Center h={300}><Loader size="lg"/></Center>;

  // Mapping Rows Tabel
  const rows = data.map((item) => (
    <Table.Tr key={item.id}>
      
      {/* Kolom Tanggal */}
      <Table.Td>
        <Text fz="sm" fw={500}>
            {new Date(item.tanggal_distribusi).toLocaleDateString('id-ID', {
                weekday: 'short', day: 'numeric', month: 'short'
            })}
        </Text>
        <Text fz="xs" c="dimmed">
            {new Date(item.tanggal_distribusi).getFullYear()}
        </Text>
      </Table.Td>

      {/* Kolom Sekolah */}
      <Table.Td>
        <Group gap="sm">
          <ThemeIcon size="lg" radius="xl" variant="light" color="blue">
             <IconBuilding size={18}/>
          </ThemeIcon>
          <div>
            <Text fz="sm" fw={500}>
                {item.sekolah?.nama_sekolah || <span style={{color:'red'}}>Sekolah Dihapus</span>}
            </Text>
            <Text fz="xs" c="dimmed">
                NPSN: {item.sekolah?.npsn || "-"}
            </Text>
          </div>
        </Group>
      </Table.Td>

      {/* Kolom Menu */}
      <Table.Td>
        <Group gap="sm">
            <ThemeIcon size="lg" radius="xl" variant="light" color="orange">
                <IconChefHat size={18} />
            </ThemeIcon>
            <div>
                <Text fz="sm" fw={500}>
                    {item.menu?.nama_menu || "Menu Dihapus"}
                </Text>
                <Badge variant="outline" color="gray" size="xs">
                    {item.jumlah_paket} Porsi
                </Badge>
            </div>
        </Group>
      </Table.Td>

      {/* Kolom Status (Badge) */}
      <Table.Td>
        <Badge 
            color={statusColors[item.status] || 'gray'} 
            variant="light"
            size="md"
        >
            {item.status.toUpperCase()}
        </Badge>
      </Table.Td>

      {/* Kolom Aksi (ActionIcon) */}
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          {item.status === 'disiapkan' ? (
            <>
              <Tooltip label="Kirim Sekarang">
                <ActionIcon variant="light" color="blue" onClick={() => handleKirim(item.id)}>
                    <IconTruckDelivery size={18} />
                </ActionIcon>
              </Tooltip>
              
              <Tooltip label="Hapus Jadwal">
                <ActionIcon variant="light" color="red" onClick={() => handleHapus(item.id)}>
                    <IconTrash size={18} />
                </ActionIcon>
              </Tooltip>
            </>
          ) : (
             <Text fz="xs" c="dimmed" fs="italic">
                {item.status === 'diterima' ? 'Selesai' : 'Dalam Perjalanan'}
             </Text>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div style={{ padding: 20 }}>
      
      <Group justify="space-between" mb="lg">
        <div>
            <Title order={2}>Jadwal Distribusi</Title>
            <Text c="dimmed" size="sm">Kelola pengiriman makanan ke sekolah tujuan</Text>
        </div>
        <Group>
            <Button variant="default" leftSection={<IconRefresh size={16}/>} onClick={fetchData}>
                Refresh
            </Button>
            <Button onClick={open} leftSection={<IconPlus size={16}/>}>
                Buat Jadwal
            </Button>
        </Group>
      </Group>

      {/* Alert jika belum punya menu */}
      {menuOptions.length === 0 && (
         <Alert icon={<IconInfoCircle size={16}/>} color="orange" mb="md" title="Peringatan">
            Anda belum memiliki data Menu. Silakan buat menu terlebih dahulu di halaman Manajemen Menu.
         </Alert>
      )}

      {/* Tabel dengan Style Baru */}
      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm">
                <Table.Thead bg="gray.0">
                    <Table.Tr>
                        <Table.Th>Tanggal</Table.Th>
                        <Table.Th>Sekolah Tujuan</Table.Th>
                        <Table.Th>Menu & Jumlah</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th ta="right">Aksi</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? rows : (
                        <Table.Tr>
                            <Table.Td colSpan={5} align="center" style={{ padding: 30 }}>
                                <Text c="dimmed">Belum ada jadwal pengiriman yang dibuat.</Text>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </ScrollArea>
      </Paper>

      {/* --- MODAL FORM --- */}
      <Modal 
        opened={opened} 
        onClose={close} 
        title={<Text fw={700}>Jadwalkan Pengiriman</Text>}
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      >
        <Box>
            <Select
                label="Sekolah Tujuan"
                placeholder="Pilih sekolah"
                data={sekolahOptions}
                searchable
                nothingFoundMessage="Tidak ditemukan"
                mb="md"
                value={form.sekolah_id}
                onChange={(val) => setForm({...form, sekolah_id: val})}
                withAsterisk
                leftSection={<IconBuilding size={16}/>}
            />
            
            <Select
                label="Menu Makanan"
                placeholder="Pilih menu"
                data={menuOptions}
                mb="md"
                value={form.menu_id}
                onChange={(val) => setForm({...form, menu_id: val})}
                withAsterisk
                leftSection={<IconChefHat size={16}/>}
            />

            <DateInput
                label="Tanggal Pengiriman"
                placeholder="Pilih tanggal"
                value={form.tanggal_distribusi}
                onChange={(val) => setForm({...form, tanggal_distribusi: val})}
                mb="md"
                withAsterisk
            />

            <NumberInput
                label="Jumlah Porsi"
                min={1}
                value={form.jumlah}
                onChange={(val) => setForm({...form, jumlah: val})}
                mb="xl"
                withAsterisk
            />

            <Group justify="flex-end">
                <Button variant="default" onClick={close} disabled={submitting}>Batal</Button>
                <Button onClick={handleSubmit} loading={submitting} leftSection={<IconPlus size={16}/>}>Simpan Jadwal</Button>
            </Group>
        </Box>
      </Modal>
    </div>
  );
}